import { supabase } from "@/lib/supabaseClient";

export const PATIENT_FILES_BUCKET = "patient-files";
export const PATIENT_FILE_MAX_BYTES = 25 * 1024 * 1024;
export const PATIENT_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
];

export const PATIENT_FILE_CATEGORIES = [
  { id: "xray_imaging", label: "X-rays / Imaging", shortLabel: "X-rays", view: "grid" },
  { id: "clinical_photos", label: "Clinical Photos", shortLabel: "Clinical Photos", view: "grid" },
  { id: "ortho_case_photos", label: "Ortho Case Photos", shortLabel: "Ortho Photos", view: "grid" },
  { id: "documents", label: "Consent Forms & Documents", shortLabel: "Documents", view: "list" },
];

const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const ORTHO_STAGES = new Set(["initial", "progress", "final"]);

export function assertPatientFileUpload(file) {
  if (!file) return;
  if (!PATIENT_FILE_TYPES.includes(file.type)) {
    throw new Error("Upload a JPG, PNG, WebP, or PDF file.");
  }
  if (file.size > PATIENT_FILE_MAX_BYTES) {
    throw new Error("Patient files must be 25MB or smaller.");
  }
}

export async function listPatientFiles(patientId) {
  const { data, error } = await supabase.rpc("list_patient_files", { p_patient_id: patientId });
  if (error) throw error;
  return normalizePatientFilesPayload(data);
}

export async function createSignedPatientFileUrls(paths, expiresInSeconds = 60 * 60) {
  const uniquePaths = Array.from(new Set((paths || []).filter(Boolean)));
  if (uniquePaths.length === 0) return {};
  const { data, error } = await supabase.storage
    .from(PATIENT_FILES_BUCKET)
    .createSignedUrls(uniquePaths, expiresInSeconds);
  if (error) throw error;
  return (data || []).reduce((acc, row) => {
    if (row?.path && row?.signedUrl) acc[row.path] = row.signedUrl;
    return acc;
  }, {});
}

export async function createSignedPatientFileUrl(path, expiresInSeconds = 15 * 60) {
  if (!path) throw new Error("File storage path is missing.");
  const urls = await createSignedPatientFileUrls([path], expiresInSeconds);
  const signedUrl = urls[path];
  if (!signedUrl) throw new Error("Could not create a preview link for this file.");
  return signedUrl;
}

export async function uploadPatientFile({
  patientId,
  clinicId,
  file,
  category,
  captureDate,
  visitId = null,
  treatmentPlanId = null,
  orthoCaseId = null,
  orthoStage = null,
}) {
  assertPatientFileUpload(file);
  if (!patientId || !clinicId) throw new Error("Patient and clinic are required.");
  if (!PATIENT_FILE_CATEGORIES.some((item) => item.id === category)) {
    throw new Error("Pick a file category.");
  }
  if (category === "ortho_case_photos" && (!orthoCaseId || !ORTHO_STAGES.has(orthoStage))) {
    throw new Error("Ortho case and stage are required before uploading Ortho Case Photos.");
  }

  const extension = extensionForFile(file);
  const objectName = `${crypto.randomUUID?.() || Date.now()}.${extension}`;
  const storagePath = `${clinicId}/${patientId}/${objectName}`;
  const bucket = supabase.storage.from(PATIENT_FILES_BUCKET);
  const uploadBody = await storageUploadBodyForFile(file);
  const uploadResult = await bucket.upload(storagePath, uploadBody, {
    cacheControl: "3600",
    contentType: file.type,
    upsert: false,
  });
  if (uploadResult.error) throw uploadResult.error;

  try {
    const { data, error } = await supabase.rpc("create_patient_file_metadata", {
      p_input: {
        patient_id: patientId,
        category,
        storage_path: storagePath,
        original_filename: file.name || objectName,
        file_kind: fileKindForMime(file.type),
        mime_type: file.type,
        size_bytes: file.size,
        capture_date: captureDate || todayDateString(),
        visit_id: visitId || null,
        treatment_plan_id: treatmentPlanId || null,
        ortho_case_id: orthoCaseId || null,
        ortho_stage: orthoStage || null,
      },
    });
    if (error) throw error;
    return data;
  } catch (error) {
    await bucket.remove([storagePath]);
    throw error;
  }
}

export async function archivePatientFile(fileId) {
  const { data, error } = await supabase.rpc("archive_patient_file", { p_file_id: fileId });
  if (error) throw error;
  return data;
}

function normalizePatientFilesPayload(payload) {
  if (!payload) return { files: [], ortho_cases: [] };
  return {
    ...payload,
    files: Array.isArray(payload.files) ? payload.files : [],
    ortho_cases: Array.isArray(payload.ortho_cases) ? payload.ortho_cases : [],
  };
}

function fileKindForMime(mimeType) {
  return IMAGE_TYPES.has(mimeType) ? "image" : "document";
}

function extensionForFile(file) {
  if (file.type === "image/jpeg") return "jpg";
  if (file.type === "image/png") return "png";
  if (file.type === "image/webp") return "webp";
  if (file.type === "application/pdf") return "pdf";
  return file.name?.split(".").pop()?.toLowerCase() || "bin";
}

async function storageUploadBodyForFile(file) {
  // Avoid Supabase Storage's File/FormData path; the app's custom fetch boundary is safer with raw bytes.
  if (typeof file?.arrayBuffer === "function") {
    return file.arrayBuffer();
  }
  return file;
}

function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}
